import { useEffect, useState } from "react";
import axiosInstance from "config/axiosInstance";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import Card from "@mui/material/Card";
import Table from "examples/Tables/Table";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import { useAppAlert } from "hooks/useAppAlert";
import { useConfirm } from "hooks/useConfirm";

export default function Sucursales() {
  const { showAlert, AlertBanner } = useAppAlert();
  const { confirm, ConfirmModal } = useConfirm();
  const [sucursales, setSucursales] = useState([]);
  const [stats, setStats] = useState([]);
  const [sinAsignar, setSinAsignar] = useState({ total_depositos: 0, total_monto: 0 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre_sucursal: "" });

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fetchSucursales = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/sucursales");
      if (res.data.success) {
        setSucursales(res.data.sucursales || []);
      }
    } catch (err) {
      showAlert("Error cargando sucursales", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get("/sucursales/stats");
      if (res.data.success) {
        setStats(res.data.stats || []);
        setSinAsignar(res.data.sin_asignar || { total_depositos: 0, total_monto: 0 });
      }
    } catch (err) {
      // Silent error
    }
  };

  useEffect(() => {
    fetchSucursales();
    fetchStats();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nombre_sucursal: "" });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ nombre_sucursal: s.nombre_sucursal || "" });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    try {
      if (!form.nombre_sucursal || form.nombre_sucursal.trim() === "") {
        showAlert("El nombre de la sucursal es obligatorio", "warning");
        return;
      }

      if (editing) {
        await axiosInstance.put(`/sucursales/${editing.id}`, form);
        showAlert("Sucursal actualizada", "success");
      } else {
        await axiosInstance.post("/sucursales", form);
        showAlert("Sucursal creada", "success");
      }

      fetchSucursales();
      fetchStats();
      handleClose();
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al guardar sucursal", "error");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("¿Seguro que deseas eliminar esta sucursal?", "Eliminar sucursal");
    if (!ok) return;
    try {
      await axiosInstance.delete(`/sucursales/${id}`);
      showAlert("Sucursal eliminada", "success");
      fetchSucursales();
      fetchStats();
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al eliminar sucursal", "error");
    }
  };

  const columns = [
    { name: "nombre", align: "left" },
    { name: "depositos", align: "center" },
    { name: "total", align: "right" },
    { name: "acciones", align: "center" },
  ];

  const rows = sucursales.map((s) => {
    const stat = stats.find((st) => st.id === s.id) || { total_depositos: 0, total_monto: 0 };
    return {
      nombre: (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {s.nombre_sucursal}
        </VuiTypography>
      ),
      depositos: (
        <VuiTypography variant="button" color="text" fontWeight="medium">
          {stat.total_depositos}
        </VuiTypography>
      ),
      total: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          S/. {formatCurrency(stat.total_monto)}
        </VuiTypography>
      ),
      acciones: (
        <VuiBox>
          <IconButton size="small" onClick={() => openEdit(s)} sx={{ color: "#0075ff" }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(s.id)} sx={{ color: "#e31a1a" }}>
            <Delete fontSize="small" />
          </IconButton>
        </VuiBox>
      ),
    };
  });

  return (
    <Card sx={{ height: "100%" }}>
      <ConfirmModal />
      <VuiBox p={3}>
        <AlertBanner />
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <VuiBox>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Sucursales
            </VuiTypography>
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Gestiona tus sucursales o tiendas
            </VuiTypography>
          </VuiBox>
          <VuiButton color="info" size="small" onClick={openCreate}>
            + Nueva Sucursal
          </VuiButton>
        </VuiBox>

        {/* Stats Summary */}
        <VuiBox mb={3} p={2} sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: "12px" }}>
          <VuiTypography variant="button" color="text" mb={1}>
            Sin asignar a sucursal
          </VuiTypography>
          <VuiBox display="flex" gap={3}>
            <VuiBox>
              <VuiTypography variant="caption" color="text">
                Depósitos
              </VuiTypography>
              <VuiTypography variant="h6" color="white" fontWeight="bold">
                {sinAsignar.total_depositos}
              </VuiTypography>
            </VuiBox>
            <VuiBox>
              <VuiTypography variant="caption" color="text">
                Total
              </VuiTypography>
              <VuiTypography variant="h6" color="white" fontWeight="bold">
                S/. {formatCurrency(sinAsignar.total_monto)}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {loading ? (
          <VuiTypography color="text">Cargando...</VuiTypography>
        ) : sucursales.length === 0 ? (
          <VuiTypography color="text" textAlign="center">
            No tienes sucursales creadas. Haz clic en "+ Nueva Sucursal"
          </VuiTypography>
        ) : (
          <Table columns={columns} rows={rows} />
        )}
      </VuiBox>

      {/* Dialog for Create/Edit */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#0f1535", color: "white" }}>
          {editing ? "Editar Sucursal" : "Nueva Sucursal"}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#0f1535", pt: 2 }}>
          <VuiBox mb={2}>
            <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
              Nombre de la Sucursal
            </VuiTypography>
            <VuiInput
              placeholder="Ej: Sucursal Centro, Tienda Norte"
              value={form.nombre_sucursal}
              onChange={(e) => setForm({ ...form, nombre_sucursal: e.target.value })}
              sx={{ width: "100%" }}
            />
          </VuiBox>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#0f1535" }}>
          <VuiButton onClick={handleClose} color="secondary" size="small">
            Cancelar
          </VuiButton>
          <VuiButton onClick={handleSubmit} color="info" size="small">
            {editing ? "Actualizar" : "Crear"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
